import { useRef, useState } from 'react';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import {
  CompanyContactType,
  companyContactTypeNames,
  type CompanyContactDialogData,
  type SaveCompanyContactPayload,
  type SaveCompanyContactResponse,
} from '@/core/models/report-settings.models';
import { formatDisplayDate } from '@/core/models/report-options.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { reportSettingsService } from '@/core/services/reportSettings';
import { parseContactDate } from './companyContactDates';

const clean = (value: string): string | null => {
  const text = (value ?? '').trim();
  return text ? text : null;
};

/** Angular CompanyContactDialogComponent: add or edit one director, secretary, solicitor or banker. */
export function CompanyContactDialog({
  data,
  dialogRef,
}: DialogComponentProps<CompanyContactDialogData, SaveCompanyContactResponse>) {
  const contact = data?.contact;
  const contactType = data?.contactType ?? CompanyContactType.Director;
  const isPerson =
    contactType === CompanyContactType.Director ||
    contactType === CompanyContactType.Secretary ||
    contactType === CompanyContactType.Solicitor;
  const usesPersonName = data?.usesPersonName !== undefined ? !!data?.usesPersonName : isPerson;
  const [fullName, setFullName] = useState(contact ? contact.fullName : '');
  const [firstName, setFirstName] = useState(contact?.firstName ?? '');
  const [lastName, setLastName] = useState(contact?.lastName ?? '');
  const savedWithoutNameParts = usesPersonName && !!contact && !contact.firstName && !contact.lastName;
  const [addressLine, setAddressLine] = useState(contact?.addressLine ?? '');
  const [addressLine2, setAddressLine2] = useState(contact?.addressLine2 ?? '');
  const [city, setCity] = useState(contact?.city ?? '');
  const [county, setCounty] = useState(contact?.county ?? '');
  const [postCode, setPostCode] = useState(contact?.postCode ?? '');
  const [country, setCountry] = useState(contact?.country ?? '');
  const [dateAppointed, setDateAppointed] = useState(contact ? formatDisplayDate(contact.dateAppointed) : '');
  const [dateCeased, setDateCeased] = useState(contact ? formatDisplayDate(contact.dateCeased) : '');
  const [ordinaryShares, setOrdinaryShares] = useState(
    contact && contact.ordinaryShares !== null && contact.ordinaryShares !== undefined
      ? `${contact.ordinaryShares}`
      : '',
  );
  const numberValue = (value: number | null | undefined) => (value === null || value === undefined ? '' : `${value}`);
  const [salary, setSalary] = useState(numberValue(contact?.salary));
  const [interestOnCapital, setInterestOnCapital] = useState(numberValue(contact?.interestOnCapital));
  const [interestOnDrawings, setInterestOnDrawings] = useState(numberValue(contact?.interestOnDrawings));
  const [utr, setUtr] = useState(contact?.utr ?? '');
  const [createAccount, setCreateAccount] = useState(contact ? contact.createAccount : false);
  const [createIndividualClient, setCreateIndividualClient] = useState(
    contact ? contact.createIndividualClient : false,
  );
  const [isSaving, setIsSaving] = useState(false);
  /** Field name -> message for every problem found by the last Save; a field's entry clears when it is edited. */
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);

  const clearError = (field: string) =>
    setErrors((current) =>
      field in current ? Object.fromEntries(Object.entries(current).filter(([key]) => key !== field)) : current,
    );

  const name = data?.principalLabel ?? companyContactTypeNames[contactType] ?? 'Contact';
  const partnerTerms = !!data?.partnerTerms;
  const heading = contact ? `Edit ${name}` : `Add New ${name}`;
  const isDirector = contactType === CompanyContactType.Director;
  const hasAppointmentDates =
    contactType === CompanyContactType.Director || contactType === CompanyContactType.Secretary;
  /**
   * BUG-078 / BUG-029: the old app's Add Contact popup for a partner, director, secretary or solicitor has County and no
   * Country, so only a banker keeps the Country box. A Country already saved on the contact is not shown but is sent
   * back unchanged on save (it stays in `country`), so editing such a contact never loses it.
   */
  const showCountry = contactType === CompanyContactType.Banker && !partnerTerms;
  /** The share is a percentage for a partner ("Share (%)") and where the screen says so ("Share%"). */
  const sharesArePercent = partnerTerms || !!data?.shareAsPercentage;

  const onSave = () => {
    if (!data?.clientId) {
      notifyError('Select a client from Accounts first.');
      return;
    }
    const found: Record<string, string> = {};
    let effectiveFirst = firstName.trim();
    let effectiveLast = lastName.trim();
    const trimmedName = fullName.trim();
    if (usesPersonName) {
      if (!effectiveLast && effectiveFirst) {
        const lastSpace = effectiveFirst.lastIndexOf(' ');
        if (lastSpace > 0) {
          effectiveLast = effectiveFirst.slice(lastSpace + 1).trim();
          effectiveFirst = effectiveFirst.slice(0, lastSpace).trim();
        }
      }
      if (!effectiveFirst) found.firstName = 'Enter the first name.';
      if (!effectiveLast) found.lastName = 'Enter the last name.';
    } else if (!trimmedName) {
      found.fullName = 'Enter the full name.';
    }
    const appointed = parseContactDate(dateAppointed);
    const ceased = parseContactDate(dateCeased);
    if (appointed === undefined) found.dateAppointed = 'Enter dates as DD/MM/YYYY.';
    if (ceased === undefined) found.dateCeased = 'Enter dates as DD/MM/YYYY.';
    if (appointed && ceased && ceased < appointed) {
      found.dateCeased = 'The date ceased cannot be before the date appointed.';
    }
    let shares: number | null = null;
    if (isDirector && `${ordinaryShares}`.trim() !== '') {
      shares = Number(`${ordinaryShares}`.replace(/,/g, '').trim());
      if (Number.isNaN(shares) || shares < 0) {
        found.ordinaryShares = 'Ordinary shares must be a number of zero or more.';
      } else if (sharesArePercent && shares > 100) {
        found.ordinaryShares = 'The share must be a percentage between 0 and 100.';
      }
    }
    const amounts: Record<string, number | null> = {};
    for (const [key, text] of Object.entries({ salary, interestOnCapital, interestOnDrawings })) {
      const value = partnerTerms && text.trim() !== '' ? Number(text.replace(/,/g, '').trim()) : null;
      if (value !== null && (Number.isNaN(value) || value < 0)) {
        found[key] = 'Salary and interest must be numbers of zero or more.';
      } else if (key !== 'salary' && value !== null && value > 100) {
        found[key] = 'Interest is a rate: enter a percentage of 100 or less.';
      }
      amounts[key] = value;
    }
    if (isDirector && utr.trim() !== '' && !/^\d{10}$/.test(utr.trim())) found.utr = 'UTR must be 10 digits.';

    setErrors(found);
    const firstInvalid = Object.keys(found)[0];
    if (firstInvalid) {
      notifyError(found[firstInvalid]);
      formRef.current?.querySelector<HTMLInputElement>(`input[name="${firstInvalid}"]`)?.focus();
      return;
    }
    const payload: SaveCompanyContactPayload = {
      id: data.contact?.id ?? null,
      clientId: data.clientId,
      contactType,
      // The API writes a person's full name from the parts itself; this is only for an API that predates them.
      fullName: usesPersonName ? [effectiveFirst, effectiveLast].filter(Boolean).join(' ') || trimmedName : trimmedName,
      firstName: usesPersonName ? effectiveFirst || null : null,
      lastName: usesPersonName ? effectiveLast || null : null,
      addressLine: clean(addressLine),
      addressLine2: clean(addressLine2),
      city: clean(city),
      county: clean(county),
      postCode: clean(postCode),
      country: clean(country),
      dateAppointed: hasAppointmentDates ? (appointed ?? null) : null,
      dateCeased: hasAppointmentDates ? (ceased ?? null) : null,
      ordinaryShares: shares,
      utr: isDirector ? clean(utr) : null,
      ...(partnerTerms ? amounts : {}),
      createAccount: isDirector && !partnerTerms && createAccount,
      createIndividualClient: isDirector && !partnerTerms && createIndividualClient,
    };

    setIsSaving(true);
    reportSettingsService.saveCompanyContact(payload).then(
      (response) => {
        const extras = [
          response.createdAccount ? "director's account created" : '',
          response.createdClient ? 'individual client created' : '',
        ].filter((x) => !!x);
        notifySuccess(
          `${name} ${payload.id ? 'updated' : 'added'} successfully${extras.length ? ` (${extras.join(', ')})` : ''}.`,
        );
        dialogRef.close(response);
      },
      (error) => {
        setIsSaving(false);
        // The API names the field of each problem it found; show those inline as well as in the toast.
        const reported = (error as { error?: { errors?: { field?: string; message?: string }[] } })?.error?.errors;
        if (Array.isArray(reported)) {
          const fromApi: Record<string, string> = {};
          for (const item of reported) {
            if (item?.field && item.message && !(item.field in fromApi)) fromApi[item.field] = item.message;
          }
          if (Object.keys(fromApi).length) setErrors(fromApi);
        }
        notifyError(error);
      },
    );
  };

  return (
    <div className="mat-dialog-wrapper">
      <DialogContent>
        <form ref={formRef} noValidate onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>{heading}</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div>
            {usesPersonName ? (
              <>
                {savedWithoutNameParts && (
                  <p className="mb-1">
                    Saved as &ldquo;{contact.fullName}&rdquo;. Enter the first name and the last name.
                  </p>
                )}
                <div className="d-flex gap-20">
                  <div className="form-field">
                    {/* BUG-078: the old app's Add New Partner popup asks for "Name" and "Last Name". */}
                    <p className="fw-bold required">{partnerTerms ? 'Name' : 'First Name'}</p>
                    <InputField
                      fieldClassName="w-200"
                      required
                      maxLength={100}
                      name="firstName"
                      charMode="name"
                      value={firstName}
                      invalid={!!errors.firstName}
                      onChange={(event) => {
                        setFirstName(event.target.value);
                        clearError('firstName');
                      }}
                    />
                    {errors.firstName && (
                      <p className="field-error" role="alert">
                        {errors.firstName}
                      </p>
                    )}
                  </div>
                  <div className="form-field">
                    <p className="fw-bold required">Last Name</p>
                    <InputField
                      fieldClassName="w-200"
                      required
                      maxLength={100}
                      name="lastName"
                      charMode="name"
                      value={lastName}
                      invalid={!!errors.lastName}
                      onChange={(event) => {
                        setLastName(event.target.value);
                        clearError('lastName');
                      }}
                    />
                    {errors.lastName && (
                      <p className="field-error" role="alert">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="form-field">
                {/* BUG-078: an LLP member keeps "Full Name"; BUG-029: a banker is a single "Name". */}
                <p className="fw-bold required">{data?.principalLabel ? 'Full Name' : 'Name'}</p>
                <InputField
                  fieldClassName="wp-100"
                  required
                  maxLength={255}
                  name="fullName"
                  charMode="name"
                  value={fullName}
                  invalid={!!errors.fullName}
                  onChange={(event) => {
                    setFullName(event.target.value);
                    clearError('fullName');
                  }}
                />
                {errors.fullName && (
                  <p className="field-error" role="alert">
                    {errors.fullName}
                  </p>
                )}
              </div>
            )}
            <div className="form-field">
              <p className="fw-bold">Address Line 1</p>
              <InputField
                fieldClassName="wp-100"
                maxLength={500}
                name="addressLine"
                value={addressLine}
                onChange={(event) => setAddressLine(event.target.value)}
              />
            </div>
            <div className="form-field">
              <p className="fw-bold">Address Line 2</p>
              <InputField
                fieldClassName="wp-100"
                maxLength={500}
                name="addressLine2"
                value={addressLine2}
                onChange={(event) => setAddressLine2(event.target.value)}
              />
            </div>
            <div className="d-flex gap-20">
              <div className="form-field">
                <p className="fw-bold">City</p>
                <InputField
                  fieldClassName="w-200"
                  maxLength={100}
                  name="city"
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                />
              </div>
              <div className="form-field">
                <p className="fw-bold">County</p>
                <InputField
                  fieldClassName="w-200"
                  maxLength={100}
                  name="county"
                  value={county}
                  onChange={(event) => setCounty(event.target.value)}
                />
              </div>
            </div>
            <div className="d-flex gap-20">
              <div className="form-field">
                <p className="fw-bold">Postal Code</p>
                <InputField
                  fieldClassName="w-200"
                  maxLength={20}
                  name="postCode"
                  charMode="alphanumeric"
                  value={postCode}
                  onChange={(event) => setPostCode(event.target.value)}
                />
              </div>
              {/* BUG-078 / BUG-029: County (above) and no Country, except for a banker. */}
              {showCountry && (
                <div className="form-field">
                  <p className="fw-bold">Country</p>
                  <InputField
                    fieldClassName="w-200"
                    maxLength={100}
                    name="country"
                    value={country}
                    onChange={(event) => setCountry(event.target.value)}
                  />
                </div>
              )}
            </div>
            {hasAppointmentDates && (
              <div className="d-flex gap-20">
                <div className="form-field">
                  <p className="fw-bold">Date Appointed</p>
                  <InputField
                    fieldClassName="w-200"
                    placeholder="DD/MM/YYYY"
                    charMode="number"
                    extraChars="/-"
                    maxLength={10}
                    name="dateAppointed"
                    value={dateAppointed}
                    invalid={!!errors.dateAppointed}
                    onChange={(event) => {
                      setDateAppointed(event.target.value);
                      clearError('dateAppointed');
                    }}
                  />
                  {errors.dateAppointed && (
                    <p className="field-error" role="alert">
                      {errors.dateAppointed}
                    </p>
                  )}
                </div>
                <div className="form-field">
                  <p className="fw-bold">Date Ceased</p>
                  <InputField
                    fieldClassName="w-200"
                    placeholder="DD/MM/YYYY"
                    charMode="number"
                    extraChars="/-"
                    maxLength={10}
                    name="dateCeased"
                    value={dateCeased}
                    invalid={!!errors.dateCeased}
                    onChange={(event) => {
                      setDateCeased(event.target.value);
                      clearError('dateCeased');
                    }}
                  />
                  {errors.dateCeased && (
                    <p className="field-error" role="alert">
                      {errors.dateCeased}
                    </p>
                  )}
                </div>
                {isDirector && data?.principalLabel !== 'Proprietor' && (
                  <div className="form-field">
                    <p className="fw-bold">
                      {partnerTerms ? 'Share (%)' : data?.shareAsPercentage ? 'Share%' : 'Ordinary Share(Current year)'}
                    </p>
                    <InputField
                      fieldClassName="w-200"
                      charMode="number"
                      allowDecimal
                      maxIntDigits={15}
                      maxDecimals={4}
                      name="ordinaryShares"
                      value={ordinaryShares}
                      invalid={!!errors.ordinaryShares}
                      onChange={(event) => {
                        setOrdinaryShares(event.target.value);
                        clearError('ordinaryShares');
                      }}
                    />
                    {errors.ordinaryShares && (
                      <p className="field-error" role="alert">
                        {errors.ordinaryShares}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            {partnerTerms && (
              <div className="d-flex gap-20">
                <div className="form-field">
                  <p className="fw-bold">Salary</p>
                  <InputField
                    fieldClassName="w-200"
                    charMode="number"
                    allowDecimal
                    maxIntDigits={15}
                    maxDecimals={4}
                    name="salary"
                    value={salary}
                    invalid={!!errors.salary}
                    onChange={(event) => {
                      setSalary(event.target.value);
                      clearError('salary');
                    }}
                  />
                  {errors.salary && (
                    <p className="field-error" role="alert">
                      {errors.salary}
                    </p>
                  )}
                </div>
                <div className="form-field">
                  <p className="fw-bold">Interest On Capital</p>
                  <InputField
                    fieldClassName="w-200"
                    charMode="number"
                    allowDecimal
                    maxIntDigits={15}
                    maxDecimals={4}
                    name="interestOnCapital"
                    value={interestOnCapital}
                    invalid={!!errors.interestOnCapital}
                    onChange={(event) => {
                      setInterestOnCapital(event.target.value);
                      clearError('interestOnCapital');
                    }}
                  />
                  {errors.interestOnCapital && (
                    <p className="field-error" role="alert">
                      {errors.interestOnCapital}
                    </p>
                  )}
                </div>
                <div className="form-field">
                  <p className="fw-bold">Interest On Drawings</p>
                  <InputField
                    fieldClassName="w-200"
                    charMode="number"
                    allowDecimal
                    maxIntDigits={15}
                    maxDecimals={4}
                    name="interestOnDrawings"
                    value={interestOnDrawings}
                    invalid={!!errors.interestOnDrawings}
                    onChange={(event) => {
                      setInterestOnDrawings(event.target.value);
                      clearError('interestOnDrawings');
                    }}
                  />
                  {errors.interestOnDrawings && (
                    <p className="field-error" role="alert">
                      {errors.interestOnDrawings}
                    </p>
                  )}
                </div>
              </div>
            )}
            {isDirector && (
              <div className="form-field">
                <p className="fw-bold">UTR</p>
                <InputField
                  fieldClassName="w-200"
                  maxLength={10}
                  name="utr"
                  charMode="number"
                  value={utr}
                  invalid={!!errors.utr}
                  onChange={(event) => {
                    setUtr(event.target.value);
                    clearError('utr');
                  }}
                />
                {errors.utr && (
                  <p className="field-error" role="alert">
                    {errors.utr}
                  </p>
                )}
              </div>
            )}
            {isDirector && !partnerTerms && (
              <div className="d-flex flex-column">
                <Checkbox
                  name="createAccount"
                  checked={createAccount}
                  onChange={(event) => setCreateAccount(event.target.checked)}
                >
                  Create Account (if not exists)
                </Checkbox>
                <Checkbox
                  name="createIndividualClient"
                  checked={createIndividualClient}
                  onChange={(event) => setCreateIndividualClient(event.target.checked)}
                >
                  Create client (Individual) to file Self Assessment (if not exists)
                </Checkbox>
              </div>
            )}
          </div>
          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={isSaving} onClick={onSave}>
              Save
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
